// ═══════════════════════════════════════════════
//   TUTORIAL · first-login onboarding window (5 slides).
//
//   Auto-opens once per browser/account on enterDesktop() when the user
//   has not seen it. Replay entry points:
//     1) ♡ HOW TO PLAY button in the taskbar (tb-r)
//     2) ✦ READ TUTORIAL AGAIN button in the About window
//
//   Persistence: S.tutorialSeen (in-memory) + 'tutorial-seen-v1' in
//   localStorage (works across reloads even without DB roundtrip). If you
//   later want cross-device persistence, wire S.tutorialSeen through
//   db.js the same way clowriQuizDone is handled.
// ═══════════════════════════════════════════════

const { S, scheduleSave, openWin, closeWin, toast } = window;

const TUTORIAL_LS_KEY = 'tutorial-seen-v1';

let _tutorialIdx = 0;
let _tutorialOnClose = null;

// ── Slide content ──────────────────────────────────────────────────────────
// Each slide: title, subtitle (optional), body (HTML allowed), mockup (HTML),
// cta (optional override for the final-step button).
const TUTORIAL_SLIDES = [
    {
        title: 'WELCOME TO KONBINI CLOWRI',
        subtitle: 'An 11-day shopkeeping diary.',
        body: `You are playing a temporary shopkeeper at <b>Konbini Clowri</b>, a tiny convenience store in <b>La Madriguera</b>, the underground city built into the rock. The shop's owner, <b>Clowri</b>, is away at Dokomi in Düsseldorf, setting up her booth for May 29.<br><br>From May 18 to May 28, you cover for her. Every email replied, every stamp claimed, every shift worked: all of it is what she comes home to.`,
        mockup: `
            <div class="wel-mock wel-mock-id">
                <img class="wel-id-logo" src="assets/icons/logo.png" alt="Konbini Clowri logo">
                <div class="wel-id-block">
                    <div class="wel-id-name">KONBINI CLOWRI</div>
                    <div class="wel-id-sub">LA MADRIGUERA · SECTOR 4</div>
                    <div class="wel-id-row">
                        <span class="wel-id-pill">11 DAYS</span>
                        <span class="wel-id-pill wel-id-pill-soft">18 MAY → 28 MAY</span>
                    </div>
                </div>
            </div>`,
    },
    {
        title: 'STAMP THE DAILY LOGBOOK',
        subtitle: 'Once per day, every day.',
        body: `Open the <b>Daily Rewards</b> window each day of the campaign and claim that day's stamp. The rule is strict: <b>one stamp per day, no make-ups.</b> Miss a day and that stamp is gone for good. You keep what you've collected so far, but the missing day stays empty.<br><br>Day 7 unlocks the <b>"Daily Visitor"</b> title. Day 11 awards +100 points and the <b>"Certified Gamer"</b> title. The daily stamp also feeds into your story arc: your number of active days affects which Konmail ending you can reach.`,
        mockup: `
            <div class="wel-mock wel-mock-daily">
                <div class="wel-daily-bar">
                    <span class="wel-daily-title">📅 DAILY REWARDS</span>
                    <span class="wel-daily-meta">3 / 11 · 37 pts</span>
                </div>
                <div class="wel-daily-grid">
                    <div class="wel-day claimed">✓</div>
                    <div class="wel-day claimed">✓</div>
                    <div class="wel-day today">🎁</div>
                    <div class="wel-day">🔒</div>
                    <div class="wel-day">🔒</div>
                    <div class="wel-day">🔒</div>
                    <div class="wel-day">🔒</div>
                    <div class="wel-day">🔒</div>
                    <div class="wel-day">🔒</div>
                    <div class="wel-day">🔒</div>
                    <div class="wel-day finale">⭐</div>
                </div>
            </div>`,
    },
    {
        title: 'READ THE INBOX. REPLY TO REGULARS.',
        subtitle: 'Five endings depend on this.',
        body: `<b>Konmail</b> is the konbini's email client. Across the 11-day arc, regulars, suppliers and a few stranger senders will write to you. Some emails are required: ignoring them costs <b>shop health</b>, character trust, and access to better endings. Others are optional flavour or daily background mail.<br><br>There are <b>five possible endings</b> on Day 11, from the warmest ("Everything In Order") to the bleakest ("Temporarily Closed"). What you reply, what you ignore, and what you investigate decides which one you get.`,
        mockup: `
            <div class="wel-mock wel-mock-inbox">
                <div class="wel-inbox-row">
                    <div class="wel-inbox-av" style="background:#e9d5ff;">✦</div>
                    <div class="wel-inbox-mid">
                        <div class="wel-inbox-from">Clowri</div>
                        <div class="wel-inbox-subj">you've got this (and also the keys)</div>
                    </div>
                    <div class="wel-inbox-badge wel-badge-new">NEW</div>
                </div>
                <div class="wel-inbox-row">
                    <div class="wel-inbox-av" style="background:#d1fae5;">🌿</div>
                    <div class="wel-inbox-mid">
                        <div class="wel-inbox-from">Fern Galloway</div>
                        <div class="wel-inbox-subj">heads up — Sector 4 this week</div>
                    </div>
                    <div class="wel-inbox-badge wel-badge-reply">REPLY ✦</div>
                </div>
                <div class="wel-inbox-row">
                    <div class="wel-inbox-av" style="background:#fef9c3;">🥐</div>
                    <div class="wel-inbox-mid">
                        <div class="wel-inbox-from">Barnabé Croissant</div>
                        <div class="wel-inbox-subj">Introduction — TiniSnack Co.</div>
                    </div>
                </div>
            </div>`,
    },
    {
        title: 'EARN POINTS. SPEND POINTS.',
        subtitle: 'Currency for the shop and the booth.',
        body: `Almost everything you do earns <b>points</b>: daily stamps, email replies, completing missions, playing minigames, pulling the gacha. Spend them in the <b>Konbini Shop</b> on desktop backgrounds, profile icons, titles, and real-world prizes you can redeem at the booth on May 29.<br><br>There's no rush. Save up for the big stuff or pull aliens with abandon. The points are yours.`,
        mockup: `
            <div class="wel-mock wel-mock-shop">
                <div class="wel-shop-bal">
                    <span class="wel-shop-bal-title">🛍 KONBINI SHOP</span>
                    <span class="wel-shop-bal-pts">🪙 240 PTS</span>
                </div>
                <div class="wel-shop-grid">
                    <div class="wel-shop-card">
                        <div class="wel-shop-img" style="background:linear-gradient(135deg,#ffb3d1,#ffd9eb);">🌸</div>
                        <div class="wel-shop-name">PINK DESKTOP</div>
                        <div class="wel-shop-price">120 PTS</div>
                    </div>
                    <div class="wel-shop-card">
                        <div class="wel-shop-img" style="background:linear-gradient(135deg,#a8e063,#d0f0a0);">⭐</div>
                        <div class="wel-shop-name">RARE TITLE</div>
                        <div class="wel-shop-price">200 PTS</div>
                    </div>
                    <div class="wel-shop-card">
                        <div class="wel-shop-img" style="background:linear-gradient(135deg,#fef3c7,#fcd34d);">🎁</div>
                        <div class="wel-shop-name">IRL PRIZE</div>
                        <div class="wel-shop-price">500 PTS</div>
                    </div>
                </div>
            </div>`,
    },
    {
        title: 'ONE LAST STEP · YOUR PROFILE',
        subtitle: "Quick personality test, then you're on the clock.",
        body: `Before your shift starts, the <b>Profile</b> setup will run you through 4 short personality questions to assign your starting <b>traits</b> and your first <b>title</b>. Takes about thirty seconds.<br><br>From there, the konbini is yours. Customise your nickname, swap your avatar, equip the titles you unlock. <b>See you at the booth on May 29.</b> Let's get started.`,
        mockup: `
            <div class="wel-mock wel-mock-prof">
                <div class="wel-prof-av">👤</div>
                <div class="wel-prof-mid">
                    <div class="wel-prof-name">YOUR NAME</div>
                    <div class="wel-prof-nick">★ NEW HIRE</div>
                    <div class="wel-prof-tags">
                        <span class="wel-prof-tag">CREATIVE</span>
                        <span class="wel-prof-tag">ORGANIZED</span>
                    </div>
                </div>
                <div class="wel-prof-pts">
                    <div class="wel-prof-pts-n">0</div>
                    <div class="wel-prof-pts-l">PTS</div>
                </div>
            </div>`,
        cta: "✦ LET'S GO",
    },
];

// ── Persistence helpers ─────────────────────────────────────────────────
function _lsTutorialSeen() {
    try { return localStorage.getItem(TUTORIAL_LS_KEY) === '1'; }
    catch (e) { return false; }
}

function _markTutorialSeen() {
    S.tutorialSeen = true;
    try { localStorage.setItem(TUTORIAL_LS_KEY, '1'); } catch (e) {}
    if (typeof scheduleSave === 'function') scheduleSave();
}

// ── Public API ──────────────────────────────────────────────────────────

// opts.onClose: optional callback fired when the user finishes or skips.
function openTutorial(opts) {
    opts = opts || {};
    _tutorialIdx = 0;
    _tutorialOnClose = opts.onClose || null;
    const win = document.getElementById('w-welcome');
    if (win) {
        const z = parseFloat(getComputedStyle(document.body).zoom) || 1;
        const vw = window.innerWidth / z;
        const w = win.offsetWidth || 480;
        win.style.top  = '20px';
        win.style.left = Math.max(20, Math.round((vw - w) / 2)) + 'px';
        win.style.maxHeight = '';
    }
    openWin('w-welcome');
    renderTutorialSlide(0);
}

function renderTutorialSlide(idx) {
    const slide = TUTORIAL_SLIDES[idx];
    if (!slide) return;
    _tutorialIdx = idx;
    const total = TUTORIAL_SLIDES.length;
    const isFirst = idx === 0;
    const isLast  = idx === total - 1;

    const stage = document.getElementById('wel-stage');
    if (!stage) return;

    stage.innerHTML = `
        <div class="wel-mockup-wrap">${slide.mockup}</div>
        <div class="wel-step-pill">STEP ${idx + 1} OF ${total}</div>
        <div class="wel-title">${slide.title}</div>
        ${slide.subtitle ? `<div class="wel-subtitle">${slide.subtitle}</div>` : ''}
        <div class="wel-body-txt">${slide.body}</div>
    `;

    // Replay the entrance animation each step so the change is felt.
    stage.style.animation = 'none';
    // Force reflow to restart animation.
    void stage.offsetWidth;
    stage.style.animation = 'popIn .35s cubic-bezier(.25,.46,.45,.94)';

    const dots = document.getElementById('wel-dots');
    if (dots) {
        dots.innerHTML = TUTORIAL_SLIDES.map((_, i) => {
            const cls = i === idx ? 'on' : (i < idx ? 'done' : '');
            return `<div class="wel-dot ${cls}" onclick="window.tutorialGoTo(${i})" title="Step ${i + 1}"></div>`;
        }).join('');
    }

    const back = document.getElementById('wel-back');
    if (back) back.style.visibility = isFirst ? 'hidden' : 'visible';

    const next = document.getElementById('wel-next');
    if (next) {
        next.innerHTML = isLast
            ? (slide.cta || '✦ FINISH')
            : 'NEXT ▸';
        next.onclick = isLast ? tutorialFinish : tutorialNext;
    }

    const skip = document.getElementById('wel-skip');
    if (skip) skip.style.visibility = isLast ? 'hidden' : 'visible';
}

function tutorialNext() {
    if (_tutorialIdx < TUTORIAL_SLIDES.length - 1) {
        renderTutorialSlide(_tutorialIdx + 1);
    }
}

function tutorialPrev() {
    if (_tutorialIdx > 0) renderTutorialSlide(_tutorialIdx - 1);
}

function tutorialGoTo(idx) {
    if (idx >= 0 && idx < TUTORIAL_SLIDES.length) renderTutorialSlide(idx);
}

function tutorialFinish() {
    _markTutorialSeen();
    closeWin('w-welcome');
    if (typeof toast === 'function') toast("You're on the clock ✦");
    _fireOnClose();
}

function tutorialSkip() {
    _markTutorialSeen();
    closeWin('w-welcome');
    _fireOnClose();
}

function _fireOnClose() {
    if (_tutorialOnClose) {
        const cb = _tutorialOnClose;
        _tutorialOnClose = null;
        try { cb(); } catch (e) { console.warn('tutorial onClose threw:', e); }
    }
}

// Called from auth.js enterDesktop(). Returns true if it opened the tutorial,
// so the caller can defer the trait test until after the tutorial closes.
function maybeShowTutorialOnEnter() {
    if (S.tutorialSeen || _lsTutorialSeen()) {
        // Already seen. Keep S.tutorialSeen consistent in case only LS was set.
        S.tutorialSeen = true;
        return false;
    }
    // Slight delay so the desktop has rendered before the modal pops in.
    setTimeout(() => {
        openTutorial({
            onClose: () => {
                // After tutorial closes, chain into the trait test if the user
                // hasn't done one yet. Mirrors the old enterDesktop behaviour.
                if (S.profileData && S.profileData.traits && S.profileData.traits.length === 0 && window.openTraitTest) {
                    setTimeout(() => {
                        window.openTraitTest();
                        if (typeof toast === 'function') toast('Set up your profile! ✦');
                    }, 400);
                }
            },
        });
    }, 1200);
    return true;
}

// ── Exports ────────────────────────────────────────────────────────────
window.openTutorial            = openTutorial;
window.tutorialNext            = tutorialNext;
window.tutorialPrev            = tutorialPrev;
window.tutorialGoTo            = tutorialGoTo;
window.tutorialFinish          = tutorialFinish;
window.tutorialSkip            = tutorialSkip;
window.maybeShowTutorialOnEnter = maybeShowTutorialOnEnter;
